import { useCookieStorage } from "@/shared/composables/use-storage";
import { computed, ref, watch, watchEffect } from "vue";
import { NgBalance, NgSession } from "@/shared/model/ng/ng-fw";
import { getNgTimeDuration } from "@/shared/ng/ng-integration";
import { usePageVisibility } from "@/shared/utils/use-page-visibility";
import { DateTimeUtils } from "@/shared/utils/date-time-utils";

const session = ref<NgSession | null>(null);
const balance = ref<NgBalance | null>(null);
const logonDate = ref<number | null>(null);
const showBalance = ref(false);
const cookiesLoaded = ref(false);
/**
 * in hallers
 */
const balanceNumeric = computed(() => (balance.value?.Balance ? parseInt(balance.value.Balance) : null));
const isLoggedIn = computed(() => !!session.value?.SessionToken);
const storage = useCookieStorage();

const { isDocumentVisible } = usePageVisibility();
const loginDurationMilliseconds = ref(0);
const refreshInterval = ref<number | null>(null);
const loginDurationFormatted = computed(() => DateTimeUtils.formatDuration(loginDurationMilliseconds.value));

export const useNgSession = () => ({
    session,
    balance,
    balanceNumeric,
    showBalance,
    isLoggedIn,
    logonDate,
    loginDurationMilliseconds,
    loginDurationFormatted,
    cookiesLoaded,
    init,
    refreshBalance,
    refreshSession,
    hasEnoughMoney,
    getMissingMoney,
    clearSession,
    showRegistrationOrLogin,
    showRegistration,
});

function clearSession() {
    session.value = null;
}

function init() {
    initFromCookies();
    setupWatchers();
}

function initFromCookies() {
    const sessionToken = storage.getItem("SessionToken");
    showBalance.value = storage.getItem("s-balance-visible") !== "false";
    if (sessionToken) {
        balance.value = {
            DisplayBalance: storage.getItem("s-balance"),
        };
        logonDate.value = parseInt(storage.getItem("s-logon-date")) || Date.now();
        session.value = {
            SessionToken: sessionToken,
            PlayerID: storage.getItem("PlayerID"),
            PlayerName: storage.getItem("s-player-name"),
        };
    }
    cookiesLoaded.value = true;
}

async function refreshBalance(force = false) {
    if (force) await ng_fw.refreshBalance();
    const newBalance = ng_fw.getDisplayBalance();
    balance.value = newBalance;
}

function refreshSession() {
    const newSession = ng_fw.getSessionDetails();
    session.value = newSession;
}

/**
 * Checks wether user has money for bet
 * @param requiredAmount value in hallers
 */
function hasEnoughMoney(requiredAmount: number) {
    return (balanceNumeric.value || 0) >= requiredAmount;
}

/**
 * Gets missing money for bet
 * @param requiredAmount value in hallers
 */
function getMissingMoney(requiredAmount: number) {
    const sub = requiredAmount - balanceNumeric.value;
    return sub < 0 ? 0 : sub;
}

function setCookie(name: string, value: string, options?: Cookies.CookieAttributes) {
    if (value == null) {
        storage.removeItem(name);
    } else {
        storage.setItem(name, value, "essential", options);
    }
}

const startLoginDurationTimer = () => {
    loginDurationMilliseconds.value = logonDate.value ? Date.now() - logonDate.value : 0;
    refreshInterval.value = window.setInterval(() => {
        loginDurationMilliseconds.value += 1000;
    }, 1000);
};

function setupWatchers() {
    watchEffect(() => setCookie("s-balance-visible", showBalance.value as any, { expires: 365 }));
    watchEffect(() => setCookie("s-balance", balance.value?.DisplayBalance));
    watchEffect(() => setCookie("s-logon-date", logonDate.value as any));
    watchEffect(() => {
        setCookie("PlayerID", session.value?.PlayerID, { sameSite: "strict" });
        setCookie("SessionToken", session.value?.SessionToken, { sameSite: "strict" });
        setCookie("s-player-name", session.value?.PlayerName);
        if (!session.value?.SessionToken) {
            balance.value = null;
            logonDate.value = null;
        } else if (session.value?.LoginTimeStamp) {
            logonDate.value = getNgTimeDuration(session.value.LoginTimeStamp, session.value.ServerTimeStamp);
        }
        addAuthenticatedBodyClass();
    });

    watch(
        [logonDate, isDocumentVisible],
        () => {
            clearInterval(refreshInterval.value);
            if (isLoggedIn.value) startLoginDurationTimer();
        },
        {
            immediate: true,
        },
    );
}

function addAuthenticatedBodyClass() {
    if (isLoggedIn.value) {
        document.body?.classList.add("authenticated");
        document.body?.classList.remove("unauthenticated");
    } else {
        document.body?.classList.remove("authenticated");
        document.body?.classList.add("unauthenticated");
    }
}

async function showRegistrationOrLogin() {
    ng_fw.showLogin();
}

function showRegistration() {
    window.location.href = `${environment.portalBaseUrl}/registrace?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`;
}
